import { jsxs, jsx } from "react/jsx-runtime";
import { Box, Text, List, Anchor, IconButton } from "@zillow/constellation-10";
import { IconFacebook, IconInstagram, IconLinkedIn, IconX } from "@zillow/constellation-icons";
const SOCIAL_LINKS = [{
  key: "facebook",
  title: "Facebook",
  href: "https://www.facebook.com/Zillow/",
  icon: /* @__PURE__ */ jsx(IconFacebook, {})
}, {
  key: "instagram",
  title: "Instagram",
  href: "https://www.instagram.com/zillow/",
  icon: /* @__PURE__ */ jsx(IconInstagram, {})
}, {
  key: "linkedin",
  title: "LinkedIn",
  href: "https://www.linkedin.com/company/zillow/",
  icon: /* @__PURE__ */ jsx(IconLinkedIn, {})
}, {
  key: "x",
  title: "X",
  href: "https://x.com/zillow/",
  icon: /* @__PURE__ */ jsx(IconX, {})
}];
const FOOTER_LOGO_MAX_WIDTH_PX = 232;
const legalLinkCss = {
  color: "text.neutral",
  cursor: "pointer",
  textDecoration: "none",
  _hover: {
    color: "text.neutral",
    textDecoration: "underline"
  },
  _focusVisible: {
    color: "text.neutral",
    textDecoration: "underline"
  },
  _visited: {
    color: "text.neutral"
  }
};
const legalCookieControlCss = {
  ...legalLinkCss,
  backgroundColor: "transparent",
  border: "none",
  padding: 0
};
const B2BFooter = ({
  logo,
  columns = [],
  disabled
}) => {
  const showCookieConsentBanner = () => {
    var _a, _b;
    (_b = (_a = window.DG_BANNER_API) == null ? void 0 : _a.showLayer) == null ? void 0 : _b.call(_a, "categories-layer");
  };
  const columnCount = columns.length;
  return /* @__PURE__ */ jsxs(Box, { "data-testid": "b2b-footer", role: "region", "aria-label": "B2B Footer", children: [
    disabled ? null : /* @__PURE__ */ jsxs(Box, { css: {
      display: "grid",
      gridTemplateColumns: "repeat(12, 1fr)",
      // Match prod footer (e.g. rentals-network): gap 24px; pad 64px 24px
      gap: "loose",
      maxWidth: "breakpoint-xxl",
      margin: "0 auto",
      paddingTop: "layout.vast.default",
      paddingBottom: "layout.vast.default",
      paddingLeft: "loose",
      paddingRight: "loose",
      borderTopWidth: "1px",
      borderTopStyle: "solid",
      borderColor: "border.muted",
      xlDown: {
        gap: "0"
      }
    }, children: [
      /* @__PURE__ */ jsx(Box, { css: {
        gridColumn: "span 3",
        xlDown: {
          gridColumn: "span 12",
          marginBottom: "loose"
        }
      }, children: logo && /* @__PURE__ */ jsx(Box, { asChild: true, css: {
        display: "block",
        flexShrink: 0,
        width: "fit-content"
      }, children: /* @__PURE__ */ jsx("img", { src: logo.src, alt: logo.alt, width: logo.width, style: {
        display: "block",
        width: logo.width,
        maxWidth: FOOTER_LOGO_MAX_WIDTH_PX,
        height: "auto"
      } }) }) }),
      /* @__PURE__ */ jsx(Box, { css: {
        gridColumn: "span 9",
        display: "grid",
        gridTemplateColumns: columnCount <= 0 ? "minmax(0, 1fr)" : columnCount === 1 ? "minmax(0, 1fr)" : columnCount === 2 ? "repeat(2, minmax(0, 1fr))" : columnCount === 3 ? "repeat(3, minmax(0, 1fr))" : columnCount === 4 ? "repeat(4, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        // Center each column block in its grid cell; text stays left-aligned inside
        justifyItems: "center",
        gap: "loose",
        width: "100%",
        minWidth: 0,
        xlDown: {
          gridColumn: "span 12",
          gridTemplateColumns: columnCount <= 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
          gap: "0",
          justifyItems: "start"
        },
        // smDown is ~320px only — too narrow; 375px phones still matched xlDown+2cols.
        // lgDown (~768px): single column to match prod mobile (stack Rental manager / Apartment, etc.).
        lgDown: {
          gridTemplateColumns: "minmax(0, 1fr)"
        }
      }, children: columns.map((column, index) => /* @__PURE__ */ jsx(Box, { css: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        minWidth: 0,
        width: "fit-content",
        maxWidth: "100%",
        xlDown: {
          width: "100%",
          marginBottom: "loose"
        }
      }, children: /* @__PURE__ */ jsxs(Box, { css: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        maxWidth: "100%",
        width: "100%",
        textAlign: "left"
      }, children: [
        /* @__PURE__ */ jsx(Text, { textStyle: "body-bold", asChild: true, children: /* @__PURE__ */ jsx("h6", { children: column.title }) }),
        column.description ? /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", css: {
          marginTop: "tighter",
          color: "text.subtle"
        }, asChild: true, children: /* @__PURE__ */ jsx("p", { children: column.description }) }) : null,
        /* @__PURE__ */ jsx(List.Root, { appearance: "none", css: {
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: "0",
          paddingTop: "0",
          width: "100%",
          textAlign: "left"
        }, children: column.links.map((link, linkIndex) => /* @__PURE__ */ jsx(List.Item, { css: {
          paddingBlock: "tighter",
          paddingInline: "0"
        }, children: /* @__PURE__ */ jsx(Anchor, { href: link.url, target: link.target, rel: link.target === "_blank" ? "noopener noreferrer" : void 0, css: {
          color: "text.link.default",
          _hover: {
            color: "text.link.hover"
          },
          _visited: {
            color: "text.link.default"
          }
        }, children: link.title }) }, link.title + "_" + linkIndex)) })
      ] }) }, column.title + "_" + index)) })
    ] }),
    /* @__PURE__ */ jsxs(Box, { "data-testid": "b2b-footer-legal", css: {
      padding: "loose",
      maxWidth: "breakpoint-xxl",
      margin: "0 auto",
      width: "100%",
      boxSizing: "border-box",
      borderTopWidth: "1px",
      borderTopStyle: "solid",
      borderColor: "border.muted",
      // main used v8 $lg_lte. Panda v10: `lg` starts at 48rem (768px); `lgDown` is *below 768px*.
      // Legacy “large” ~1024px matches `xl` / `xlDown` (max-width ~63.9975rem), not `lgDown`.
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexWrap: "wrap",
      xlDown: {
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: "tighter"
      },
      smDown: {
        paddingLeft: "0",
        paddingRight: "0"
      }
    }, children: [
      /* @__PURE__ */ jsxs(Box, { css: {
        display: "flex",
        gap: "default",
        alignItems: "stretch",
        flexWrap: "wrap",
        minWidth: 0,
        xlDown: {
          flexDirection: "column",
          alignItems: "center",
          gap: "default"
        }
      }, children: [
        /* @__PURE__ */ jsxs(Text, { textStyle: "body-sm", css: {
          xlDown: {
            textAlign: "center"
          }
        }, children: [
          "© ",
          /* @__PURE__ */ jsx("span", { children: (/* @__PURE__ */ new Date()).getFullYear() }),
          " Zillow Group, Inc. and its subsidiaries. All rights reserved."
        ] }),
        /* @__PURE__ */ jsxs(Box, { css: {
          display: "flex",
          gap: "default",
          flexWrap: "wrap",
          xlDown: {
            justifyContent: "center"
          }
        }, children: [
          /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", asChild: true, children: /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/corporate/zg-privacy-notice/", rel: "noopener noreferrer", css: legalLinkCss, children: "Privacy Notice" }) }),
          /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", asChild: true, children: /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/corporate/zg-privacy-notice/#third-party", rel: "noopener noreferrer", css: legalLinkCss, children: "Ad Choices" }) }),
          /* @__PURE__ */ jsx(Text, { textStyle: "body-sm", asChild: true, css: legalCookieControlCss, children: /* @__PURE__ */ jsx("button", { type: "button", className: "cookie-preference", "data-testid": "b2b-footer-cookie-preferences", onClick: () => showCookieConsentBanner(), children: "Cookie Preferences" }) })
        ] })
      ] }),
      disabled ? null : /* @__PURE__ */ jsx(Box, { role: "group", "aria-label": "Social media links", css: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "center",
        gap: "tightest",
        "& a": {
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minWidth: "600",
          minHeight: "600",
          flexShrink: 0
        }
      }, children: SOCIAL_LINKS.map(({
        key,
        title,
        href,
        icon
      }) => /* @__PURE__ */ jsx(IconButton, { asChild: true, emphasis: "bare", icon, shape: "circle", size: "sm", title, children: /* @__PURE__ */ jsx("a", { href, rel: "nofollow noopener noreferrer", target: "_blank", "aria-label": title }) }, key)) })
    ] })
  ] });
};
export {
  B2BFooter as default
};
